import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanAlarmDevice extends FloorplanProductItem<AlarmDeviceConfiguration> {
  @Exclude()
  private readonly _alarmDevice: AlarmDeviceConfiguration;

  @IsString()
  @Expose({ name: "alarmDeviceId" })
  private readonly _alarmDeviceId: string;

  constructor(
    floorplan: Floorplan,
    alarmDevice: AlarmDeviceConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, alarmDevice, id, x, y, size, notes);
    this._alarmDeviceId = alarmDevice?.id;
    this._alarmDevice = alarmDevice;
  }

  get alarmDevice(): AlarmDeviceConfiguration {
    return this._alarmDevice;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.alarmDevicesItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanAlarmDevice) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanAlarmDevice.create(this._floorplan, config, x, y, size);
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshAlarmDevicesLock();
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.alarmDevices;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanAlarmDevice,
        this._floorplan.alarmDevices,
        FloorplanEventType.ALARM_DEVICE_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanAlarmDevice) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteAlarmDevice(this);
  }

  static create(
    floorplan: Floorplan,
    alarmDevice: AlarmDeviceConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanAlarmDevice {
    return new FloorplanAlarmDevice(floorplan, alarmDevice, uuidv4(), x, y, size);
  }

  protected override getConfigFromProject(floorplan: Floorplan): AlarmDeviceConfiguration | undefined {
    return floorplan.getAlarmDeviceConfig(this._alarmDeviceId);
  }
}
